import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useFragment } from "react-relay";

import { setCourseIdAtom } from "@/features/BookingWizard/atoms/actions/setCourse/setCourseAtom";
import { courseIdSelectorAtom } from "@/features/BookingWizard/atoms/selectors/courseIdSelectorAtom/courseIdSelectorAtom";
import { DurationSelectorAtom } from "@/features/BookingWizard/atoms/selectors/durationSelectorAtom/durationSelectorAtom";
import { GroupSizeSelectorAtom } from "@/features/BookingWizard/atoms/selectors/groupSizeSelectorAtom/groupSizeSelectorAtom";
import { SelectableTile } from "@whoosh/design-system/src/components/controls";
import { Text, VStack } from "@whoosh/design-system/src/components/primitive";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";

import { CourseControlNode$key } from "./__generated__/CourseControlNode.graphql";
import { useGetCourseOptionsFromValidCustomerBookingSelections } from "./hooks/useGetCourseOptionsFromValidCustomerBookingSelections";

type Props = {
  nodeRef: CourseControlNode$key | null;
};

export const CourseControl = ({ nodeRef }: Props) => {
  const data = useFragment(CourseControlFragmentString, nodeRef);

  const isInBucket = data?.course?.courseBucket?.id;
  const customerCanSelectCourt =
    isInBucket &&
    data?.course?.facility?.publicBookingPermissionSet?.customerCanSelectCourt;

  const currentCourseId = useAtomValue(courseIdSelectorAtom);
  const setCourseId = useSetAtom(setCourseIdAtom);
  const currentDuration = useAtomValue(DurationSelectorAtom);
  const currentGroupSize = useAtomValue(GroupSizeSelectorAtom);

  const courseOptions = useGetCourseOptionsFromValidCustomerBookingSelections(
    data ?? null,
    currentDuration ?? 0,
    currentGroupSize
  );
  const courseNoun = getNounOverride(
    data?.course?.facility?.copyOverrides?.course?.singular,
    t`Course`
  );

  return customerCanSelectCourt ? (
    <VStack
      marginTop={2}
      backgroundColor="surface-primary"
      paddingHorizontal={5}
      paddingBottom={2}
    >
      <Text variant="mainBaseSemiBold" color="text-primary" marginVertical={4}>
        {t`Select ${courseNoun}`}
      </Text>
      <VStack gap={2}>
        {courseOptions
          ?.sort((a, b) => ((a.label || "") > (b.label || "") ? 1 : -1)) //sort label alphabetically
          .map((course) => (
            <SelectableTile
              key={course.value}
              title={course.label ?? ""}
              subtitle={
                course.description
                  ? course.description.length >= 40
                    ? course.description.substring(0, 40) + "..."
                    : course.description
                  : ""
              }
              align="center"
              isDisabled={course.isDisabled}
              isSelected={currentCourseId === course.value}
              setSelected={() => {
                setCourseId({
                  courseId: course.value ?? "",
                  startSlotId: course.startSlotId ?? "",
                });
              }}
            />
          ))}
      </VStack>
    </VStack>
  ) : null;
};

export const CourseControlFragmentString = graphql`
  fragment CourseControlNode on Node {
    ... on TimeSlot {
      ...useGetCourseOptionsFromValidCustomerBookingSelections_TimeSlot
      id
      time
      agenda {
        id
      }
      endSlotOptions(
        bookingId: $bookingId
        bookingType: $bookingType
        memberId: $memberId
      ) {
        edges {
          durationMins
          allowedPlayerCounts
          node {
            id
            time
            availability
          }
        }
      }

      course {
        id
        name
        description
        facility {
          publicBookingPermissionSet {
            customerCanSelectCourt
          }
          copyOverrides {
            course {
              singular
            }
          }
        }
        courseBucket {
          id
          slotDurations(agendaId: $agendaId, startsAt: $startTime) {
            startSlot {
              id
              time
              endSlotOptions(
                bookingId: $bookingId
                bookingType: $bookingType
                memberId: $memberId
              ) {
                edges {
                  durationMins
                  allowedPlayerCounts
                  node {
                    id
                    time
                    availability
                  }
                }
              }
            }
            maxDurationMins
          }
        }
      }
    }
  }
`;
